import type { PageResponseType, RequestPageParams, ResponseType, SysProject, User } from '@/types/common';
import httpUtil from '@/utils/http';

const request = httpUtil.mfd.request;
const prefix = '/api/v1';

export const projectApi = {
  /**
   * @description 获取所有项目
   */
  getAllProjects(): ResponseType<SysProject[]> {
    return request({
      method: 'GET',
      url: `${prefix}/allSysProjects`,
    });
  },

  /**
   * @description 获取当前角色所在项目列表
   */
  getUserSysProject(): ResponseType<{ projectId: number; projectName: string }[]> {
    return request({
      method: 'GET',
      url: `${prefix}/userSysProjects`,
    });
  },

  /**
   * @description 获取系统项目列表
   */
  getList(
    params: RequestPageParams & {
      projectName?: string;
      projectCode?: string;
      projectType?: number;
      status?: number;
    },
  ): PageResponseType<SysProject> {
    return request({
      method: 'GET',
      url: `${prefix}/sysProjects`,
      params,
    });
  },

  /**
   * @description 搜索系统项目（按名称），用于覆盖率列表页项目选择
   * 后端示例：GET /api/v1/sysProjects?pageIndex=1&pageSize=10&name=关键词
   */
  searchSysProjects(params: { pageIndex: number; pageSize: number; name?: string }): ResponseType {
    return request({
      method: 'GET',
      url: `${prefix}/sysProjects`,
      params,
    });
  },

  /**
   * @description 获取系统项目详情
   */
  getInfo(id: number): ResponseType<SysProject> {
    return request({
      method: 'GET',
      url: `${prefix}/sysProject/${id}`,
    });
  },

  /**
   * @description 增加系统项目
   */
  add(data: SysProject): ResponseType {
    return request({
      method: 'POST',
      url: `${prefix}/sysProject`,
      data,
    });
  },

  /**
   * @description 修改系统项目
   */
  update(id: number, data: SysProject): ResponseType {
    return request({
      method: 'PUT',
      url: `${prefix}/sysProject/${id}`,
      data,
    });
  },

  /**
   * @description 删除系统项目
   */
  del(id: number): ResponseType {
    return request({
      method: 'DELETE',
      url: `${prefix}/sysProject/${id}`,
    });
  },

  /**
   * @description
   */
  moveProject(data: { moveId: number; targetId: number; isBottom: boolean }): ResponseType {
    return request({
      method: 'PUT',
      url: `${prefix}/sysProject/move`,
      data,
    });
  },

  /**
   * @description 生成项目密钥
   */
  genSecret(projectId: number): ResponseType {
    return request({
      method: 'POST',
      url: `${prefix}/project/${projectId}/secret`,
    });
  },

  /**
   * 获取项目属性：类型、状态、阶段
   *
   * @param {*} { typ: string }
   * @return {*}  {ResponseType}
   */
  getProjectAttrs(): ResponseType {
    return request({
      method: 'GET',
      url: `${prefix}/sysProject/attr`,
    });
  },

  /**
   * @description 获取项目类型的角色列表
   */
  getProjectTypeUser(params?: { roleId: string[] }): ResponseType<Record<string, User[]>> {
    return request({
      method: 'GET',
      url: `${prefix}/userRole`,
      params,
    });
  },

  /**
   * @description 获取项目用户成员
   */
  getProjectUser(sysProjectId: number): ResponseType {
    return request({
      method: 'GET',
      url: `${prefix}/sysProject/${sysProjectId}/users`,
    });
  },

  /**
   * @description 更新项目用户成员
   */
  updateProjectUser(sysProjectId: number, data: { users: number[] }): ResponseType {
    return request({
      method: 'PUT',
      url: `${prefix}/sysProject/${sysProjectId}/users`,
      data,
    });
  },
  /**
   * @description 获取售后项目列表
   */
  getAfterSaleProjects(): ResponseType<SysProject[]> {
    return request({
      method: 'GET',
      url: `${prefix}/sale/projectConfig`,
    });
  },
  /**
   * 一次拿齐系统项目、产品线的配置信息
   *
   * @param {{
   *     lineId: number[];
   *     projectId: number[];
   *     projectClosed: boolean;
   *   }} {
   *     lineId,
   *     projectId,
   *     projectClosed,
   *   }
   * @return {*}  {ResponseType}
   */
  productConfig({
    lineId,
    projectId,
    projectClosed,
  }: {
    lineId?: number[];
    projectId?: number[];
    projectClosed?: boolean;
  }): ResponseType {
    const params =
      projectClosed !== undefined
        ? {
            productLineId: lineId,
            sysProjectId: projectId,
            projectClosed: projectClosed,
          }
        : {
            productLineId: lineId,
            sysProjectId: projectId,
          };
    return request({
      url: `${prefix}/productConfig`,
      method: 'GET',
      params: params,
    });
  },
  /**
   * 一次拿齐系统项目、产品线的信息
   *
   * @param {{
   *     lineId: number[];
   *     projectId: number[];
   *     projectClosed: boolean; // 项目是否关闭
   *   }} {
   *     lineId,
   *     projectId,
   *     projectClosed,
   *   }
   * @return {*}  {ResponseType}
   */
  detail({
    lineId,
    projectId,
    projectClosed,
  }: {
    lineId: number[];
    projectId: number[];
    projectClosed: boolean;
  }): ResponseType {
    const params =
      projectClosed !== undefined
        ? {
            lineId: lineId,
            sysProjectId: projectId,
            projectClosed: projectClosed,
          }
        : {
            lineId: lineId,
            sysProjectId: projectId,
          };
    return request({
      url: `${prefix}/productDetail`,
      method: 'GET',
      params: params,
    });
  },
  /**
   * 项目数量统计（分状态）
   *
   * @param {string} type
   * @return {*}  {ResponseType}
   */
  projectTypeCount(type?: string): ResponseType {
    return request({
      url: `${prefix}/statistic/projectTypeCount${type ? '?type=+' + type : ''}`,
      method: 'GET',
    });
  },

  getFocusHProjects(): ResponseType<SysProject[]> {
    return request({
      url: `${prefix}/focusHProjects`,
      method: 'GET',
    });
  },
  addFocusHProject(sysProjectId: number, index: number): ResponseType {
    return request({
      url: `${prefix}/addFocusHProject`,
      method: 'POST',
      data: { sysProjectId, index },
    });
  },
  removeFocusHProject(sysProjectId: number): ResponseType {
    return request({
      url: `${prefix}/removeFocusHProject`,
      method: 'POST',
      data: { sysProjectId },
    });
  },
  moveFocusHProject(data: { moveId: number; targetId: number }): ResponseType {
    return request({
      url: `${prefix}/moveFocusHProject`,
      method: 'PUT',
      data,
    });
  },
};
